class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getConfiguredBlogs() {
    // 先尝试查找 main-help-center-search section
    let helpCenterSection = document.querySelector('[data-section-type="main-help-center-search"]');
    
    // 如果没找到，尝试查找 main-help-center section
    if (!helpCenterSection) {
      helpCenterSection = document.querySelector('[data-section-type="main-help-center"]');
    }
    
    if (!helpCenterSection) return [];

    // 获取JSON格式的博客配置数据
    const configuredBlogsContainer = helpCenterSection.querySelector('[data-configured-blogs]');
    if (configuredBlogsContainer) {
      try {
        const blogsData = JSON.parse(configuredBlogsContainer.dataset.configuredBlogs);
        return blogsData.map(blog => blog.handle);
      } catch (e) {
        console.error('Error parsing configured blogs:', e);
        return [];
      }
    }

    return [];
  }

  getConfiguredBlogsData() {
    // 获取完整的博客数据（包括标题和URL）
    // 先尝试查找 main-help-center-search section
    let helpCenterSection = document.querySelector('[data-section-type="main-help-center-search"]');
    
    // 如果没找到，尝试查找 main-help-center section
    if (!helpCenterSection) {
      helpCenterSection = document.querySelector('[data-section-type="main-help-center"]');
    }
    
    if (!helpCenterSection) {
      console.log('No help center section found');
      return [];
    }

    const configuredBlogsContainer = helpCenterSection.querySelector('[data-configured-blogs]');
    
    if (configuredBlogsContainer) {
      try {
        const parsedData = JSON.parse(configuredBlogsContainer.dataset.configuredBlogs);
        console.log('Found configured blogs:', parsedData);
        return parsedData;
      } catch (e) {
        console.error('Error parsing configured blogs data:', e);
        return [];
      }
    }

    console.log('No configured blogs container found');
    return [];
  }

  populateHelpCenterCategories(resultsMarkup) {
    // 获取配置的博客数据
    const blogsData = this.getConfiguredBlogsData();
    
    if (blogsData.length === 0) {
      console.log('No blogs data found');
      return;
    }

    // 创建分类HTML
    const categoriesHtml = blogsData.map(blog => 
      `<li class="help-center-predictive-search__category-item">
        <a href="${blog.url}" class="help-center-predictive-search__category-link">
          ${blog.title}
        </a>
      </li>`
    ).join('');

    // 查找分类列表元素并更新
    const categoriesList = document.querySelector('#help-center-categories-list');
    
    if (categoriesList) {
      categoriesList.innerHTML = categoriesHtml;
      console.log('Categories list updated with', blogsData.length, 'items');
    } else {
      console.log('Categories list element not found');
    }

    // 过滤右侧文章列表
    this.filterArticlesByConfiguredBlogs(blogsData);
  }

  filterArticlesByConfiguredBlogs(blogsData) {
    // 获取配置的博客handles
    const configuredHandles = blogsData.map(blog => blog.handle);
    console.log('Configured blog handles for filtering:', configuredHandles);

    // 查找文章列表
    const articlesList = document.querySelector('.help-center-predictive-search__articles-list');
    console.log('Articles list found:', articlesList);

    if (!articlesList) {
      console.log('Articles list not found');
      return;
    }

    // 获取所有文章项
    const articleItems = articlesList.querySelectorAll('.help-center-predictive-search__article-item');
    console.log('Total article items found:', articleItems.length);

    let visibleCount = 0;

    // 遍历每个文章项，检查是否属于配置的博客
    articleItems.forEach((item, index) => {
      const articleLink = item.querySelector('.help-center-predictive-search__article-link');
      if (articleLink) {
        const href = articleLink.getAttribute('href');
        console.log(`Article ${index + 1} href:`, href);
        
        // 从URL中提取博客handle
        const blogHandle = this.extractBlogHandleFromUrl(href);
        console.log(`Article ${index + 1} blog handle:`, blogHandle);
        
        if (configuredHandles.includes(blogHandle)) {
          item.style.display = 'block';
          visibleCount++;
          console.log(`Article ${index + 1} is visible (matches configured blog)`);
        } else {
          item.style.display = 'none';
          console.log(`Article ${index + 1} is hidden (doesn't match configured blog)`);
        }
      }
    });

    console.log(`Filtered articles: ${visibleCount} visible out of ${articleItems.length} total`);

    // 如果没有可见文章，显示"无结果"消息
    const noResultsMsg = document.querySelector('.help-center-predictive-search__no-results');
    if (visibleCount === 0) {
      if (!noResultsMsg) {
        const articlesContainer = document.querySelector('.help-center-predictive-search__articles');
        if (articlesContainer) {
          const noResults = document.createElement('p');
          noResults.className = 'help-center-predictive-search__no-results';
          noResults.textContent = 'No articles found in configured categories';
          articlesContainer.appendChild(noResults);
        }
      }
    } else if (noResultsMsg) {
      noResultsMsg.remove();
    }
  }

  extractBlogHandleFromUrl(url) {
    // 从URL中提取博客handle，例如：/blogs/meine-bestellung-und-zahlung/article-title
    const match = url.match(/\/blogs\/([^\/]+)/);
    return match ? match[1] : null;
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      // 如果是帮助中心搜索，也需要填充分类列表
      if (this.classList.contains('help-center-predictive-search')) {
        setTimeout(() => {
          this.populateHelpCenterCategories(this.cachedResults[queryKey]);
        }, 0);
      }
      return;
    }

    // 检查是否是帮助中心搜索
    const isHelpCenterSearch = this.classList.contains('help-center-predictive-search');
    const sectionId = isHelpCenterSearch ? 'help-center-predictive-search' : 'predictive-search';

    // 如果是帮助中心搜索，获取配置的博客信息
    let url = `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=${sectionId}`;
    
    // 帮助中心搜索不需要URL参数，JavaScript会处理过滤

    fetch(url, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const sectionSelector = isHelpCenterSearch ? '#shopify-section-help-center-predictive-search' : '#shopify-section-predictive-search';
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector(sectionSelector).innerHTML;
        
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
        
        // 如果是帮助中心搜索，在DOM更新后动态填充分类列表
        if (isHelpCenterSearch) {
          // 使用 setTimeout 确保DOM已经更新
          setTimeout(() => {
            this.populateHelpCenterCategories(resultsMarkup);
          }, 0);
        }
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) {
      console.log('Status element not found, skipping live region update');
      return;
    }
    
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      if (this.statusElement) {
        this.statusElement.setAttribute('aria-hidden', 'true');
      }
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const countElement = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (countElement) {
      this.setLiveRegionText(countElement.textContent);
    }
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
